import styles from './SearchMenu.module.scss';
import SearchSuggestions from './SearchSuggestions';
import { usePageData } from '@components/context';
import { useMedia } from '@components/hooks';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  useFormStore,
  Button,
  FormInput,
  Form,
  FormSubmit,
} from '@ariakit/react';
import { FontAwesomeProIcon } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import { nanoid } from 'nanoid';

// Changes here also need to be made in SearchForm.js
const triggerAnimation = setTriggerShake => {
  setTriggerShake(true);
  setTimeout(() => {
    setTriggerShake(false);
  }, 500);
};

const SearchMenu = forwardRef(
  (
    { isSearchOpen, handleSearchOpen, setDisplayOnlyActiveItem, className },
    ref,
  ) => {
    const { rootLevel, dropdownItems: dropdownMenu } = usePageData();
    const [triggerShake, setTriggerShake] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const searchMenuRefs = useRef({ inputRef: null, suggestionsRef: [] });
    const isDesktop = useMedia('(min-width: 768px)');
    const suggestionsListId = useMemo(() => nanoid(), []);
    const [values, setValues] = useState({ term: '' });
    const form = useFormStore({ values, setValues });

    form.useValidate(() => {
      if (!values.term && isSearchOpen) {
        triggerAnimation(setTriggerShake);
        form.setError('term', 'Bitte geben Sie einen Suchbegriff an');
      }
    });

    form.useSubmit(() => {
      const isBusiness = rootLevel?.slug === 'business';
      const prefix = isBusiness ? '/business' : '';
      const filter = isBusiness ? 'business' : 'alle';

      window.open(
        `${prefix}/?s=${values.term}&filter=${filter}&limit=50`,
        '_self',
      );
    });

    const clearSearch = () => form.setValue('term', '');

    const handleKeyPress = event => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        searchMenuRefs.current.suggestionsRef[0].focus();
      }
    };

    useEffect(() => {
      // close search bar when clicked element not form element or child of it when desktop screen
      const handleClickOutside = ({ target }) => {
        if (isDesktop && !ref.current?.contains(target)) {
          handleSearchOpen(false);
        }
      };
      // close search bar when focused element not form element or child of it when desktop screen
      const handleFocus = ({ target }) => {
        if (isDesktop && !ref.current?.contains(target)) {
          handleSearchOpen(false);
        } else {
          handleSearchOpen(true);
        }
      };

      window.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('focusin', handleFocus);

      return () => {
        window.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('focusin', handleFocus);
      };
    }, [isDesktop]);

    useEffect(() => {
      if (isSearchOpen) {
        return;
      }
      setDisplayOnlyActiveItem(false);
      searchMenuRefs.current.inputRef.blur();
      form.setValue('term', '');
    }, [isSearchOpen]);

    const comboboxProps = useMemo(() => {
      if (!dropdownMenu) {
        return {};
      }
      return {
        role: 'combobox',
        'aria-controls': suggestionsListId,
        'aria-expanded': isSearchOpen,
        'aria-haspopup': 'listbox',
        'aria-autocomplete': 'none',
      };
    }, [dropdownMenu, suggestionsListId, isSearchOpen]);

    return (
      <Form
        aria-label="Suche"
        className={classNames(
          styles.formContainer,
          {
            [styles.isOpen]: isSearchOpen,
            [styles.shakeAnimation]: triggerShake,
            'shake-animation': triggerShake,
          },
          className,
        )}
        ref={ref}
        resetOnSubmit={false}
        role="search"
        store={form}
        validateOnBlur={false}
        validateOnChange={false}
      >
        <FontAwesomeProIcon
          aria-hidden
          className={classNames(styles.magnifyingGlassIcon, {
            [styles.isOpen]: isSearchOpen,
          })}
          icon="search"
          role="presentation"
          type="solid"
        />
        <FormInput
          store={form}
          {...comboboxProps}
          autoComplete="off"
          className={classNames(styles.inputContainer, {
            [styles.isOpen]: isSearchOpen,
          })}
          name="term"
          onFocus={() => handleSearchOpen(true)}
          onKeyDown={handleKeyPress}
          placeholder="Suche"
          ref={el => {
            searchMenuRefs.current.inputRef = el;
          }}
          type="search"
        />
        {isSearchOpen && values.term.length > 0 && (
          <Button
            aria-label="Sucheingabe löschen"
            className={styles.clearSearchButton}
            onClick={clearSearch}
            type="reset"
          >
            <FontAwesomeProIcon icon="times" role="presentation" type="solid" />
          </Button>
        )}
        {isSearchOpen && (
          <FormSubmit
            aria-label="Suche starten"
            className={styles.submitButton}
            render={
              <Button>
                <FontAwesomeProIcon
                  className={styles.submitIcon}
                  icon="chevron-right"
                  role="presentation"
                />
              </Button>
            }
            store={form}
          />
        )}
        {dropdownMenu && (
          <SearchSuggestions
            id={suggestionsListId}
            isSearchOpen={isSearchOpen}
            ref={searchMenuRefs}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            suggestions={dropdownMenu}
          />
        )}
      </Form>
    );
  },
);

SearchMenu.displayName = 'SearchMenu';

export default SearchMenu;
